/* eslint-disable no-type-assertion/no-type-assertion -- CopilotKit Message and F0Message are structurally identical; casts pin that at the boundary. */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useCopilotChatInternal, useCopilotContext } from '@copilotkit/react-core'
import { type Message, randomId } from '@copilotkit/shared'

import { type AppendMessage, type AppendToolCall, type F0Message } from '@factorialco/f0-react'

import { fetchThreadMessages } from 'modules/ai/chat/history/fetchThreadMessages'
import { copilotMessagesToF0, f0MessageToCopilot } from 'modules/ai/chat/protocol/messageAdapter'

function toCopilotMessage(input: AppendMessage): Message {
  if (input.role === 'tool') {
    return {
      id: randomId(),
      role: 'tool',
      content: input.content,
      toolCallId: input.toolCallId,
    } as Message
  }

  return {
    id: randomId(),
    role: input.role,
    content: input.content,
    ...(input.toolCalls && {
      toolCalls: input.toolCalls.map((tc: AppendToolCall) => ({
        id: tc.id ?? randomId(),
        type: 'function' as const,
        function: tc.function,
      })),
    }),
  }
}

// Backend only accepts user/assistant; role:"tool" is client-side
// bookkeeping and the assistant message already carries the toolCalls.
function filterPersistableMessages(messages: Message[]): Message[] {
  return messages.filter((m) => m.role === 'user' || m.role === 'assistant')
}

export type FactorialChatRuntime = {
  messages: F0Message[]
  inProgress: boolean
  threadId: string
  runtimeFetch: typeof fetch
  onBeforeSendMessage?: () => boolean | Promise<boolean>

  sendMessage: (message: string | F0Message) => void
  /** @param options.persist Persist to the backend thread. Defaults to true. */
  appendMessages: (messages: AppendMessage[], options?: { persist?: boolean }) => void
  /** Replace messages in one shot — avoids the clear() + append() race. */
  clearAndAppend: (messages: AppendMessage[]) => void
  /** Also rotates the thread id. */
  clear: () => void
  loadThread: (threadId: string, title: string) => void

  currentThreadTitle: string | null
  isLoadingThread: boolean
}

const FactorialChatRuntimeContext = createContext<FactorialChatRuntime | null>(null)

const fallbackFetch: typeof fetch = (() =>
  Promise.reject(
    new Error('useFactorialChatRuntime: no <FactorialChatRuntimeProvider> mounted')
  )) as unknown as typeof fetch

const NO_PROVIDER_FALLBACK: FactorialChatRuntime = {
  messages: [],
  inProgress: false,
  threadId: '',
  runtimeFetch: typeof fetch !== 'undefined' ? fetch : fallbackFetch,
  onBeforeSendMessage: undefined,
  sendMessage: () => {},
  appendMessages: () => {},
  clearAndAppend: () => {},
  clear: () => {},
  loadThread: () => {},
  currentThreadTitle: null,
  isLoadingThread: false,
}

export type FactorialChatRuntimeProviderProps = {
  children: ReactNode
  runtimeFetch: typeof fetch
  onBeforeSendMessage?: () => boolean | Promise<boolean>
}

export const FactorialChatRuntimeProvider = ({
  children,
  runtimeFetch,
  onBeforeSendMessage,
}: FactorialChatRuntimeProviderProps) => {
  const {
    reset,
    messages: ckMessages,
    setMessages,
    sendMessage: ckSendMessage,
    threadId,
    isLoading,
  } = useCopilotChatInternal()
  const { setThreadId, copilotApiConfig, actions } = useCopilotContext()

  const [currentThreadTitle, setCurrentThreadTitle] = useState<string | null>(null)
  const [isLoadingThread, setIsLoadingThread] = useState(false)

  // Refs avoid stale closures across the long-lived callbacks below.
  const messagesRef = useRef(ckMessages)
  messagesRef.current = ckMessages
  const threadIdRef = useRef(threadId)
  threadIdRef.current = threadId
  const onBeforeSendMessageRef = useRef(onBeforeSendMessage)
  onBeforeSendMessageRef.current = onBeforeSendMessage
  const runtimeFetchRef = useRef(runtimeFetch)
  runtimeFetchRef.current = runtimeFetch

  const clear = useCallback(() => {
    reset()
    setThreadId(randomId())
    setCurrentThreadTitle(null)
    setIsLoadingThread(false)
  }, [reset, setThreadId])

  const loadThread = useCallback(
    (newThreadId: string, title: string) => {
      setMessages([])
      setIsLoadingThread(true)
      setCurrentThreadTitle(title)
      setThreadId(newThreadId)
      void fetchThreadMessages(
        copilotApiConfig.chatApiEndpoint,
        copilotApiConfig.headers,
        newThreadId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        actions as Record<string, { render?: (...args: any[]) => any }>,
        runtimeFetchRef.current
      )
        .then(
          (msgs) => setMessages(msgs),
          () => {} // Best-effort: if it fails, stay empty
        )
        .finally(() => setIsLoadingThread(false))
    },
    [setMessages, setThreadId, copilotApiConfig, actions]
  )

  const sendMessage = useCallback<FactorialChatRuntime['sendMessage']>(
    (message) => {
      void (async () => {
        const gate = onBeforeSendMessageRef.current
        if (gate && (await gate()) === false) {
          return
        }

        const messageToSend: Message =
          typeof message === 'string'
            ? {
                id: randomId(),
                role: 'user',
                content: message,
              }
            : f0MessageToCopilot(message)

        ckSendMessage(messageToSend)
      })()
    },
    [ckSendMessage]
  )

  const appendMessages = useCallback<FactorialChatRuntime['appendMessages']>(
    (msgs, options) => {
      const persist = options?.persist ?? true

      // Generate IDs for CopilotKit and convert to Message[]
      const hydrated = msgs.map(toCopilotMessage)

      // Client-side injection. Existing messages may carry non-serializable
      // generativeUI/render functions — strip them via JSON roundtrip
      // since setMessages internally uses structuredClone.
      const current = messagesRef.current
      if (current.length === 0) {
        setMessages(hydrated)
      } else {
        const serializable = JSON.parse(JSON.stringify(current))
        setMessages([...serializable, ...hydrated])
      }

      if (!persist) return
      const currentThreadId = threadIdRef.current
      const persistable = filterPersistableMessages(hydrated)
      if (currentThreadId && persistable.length > 0) {
        void runtimeFetchRef.current(
          `${copilotApiConfig.chatApiEndpoint}/chat-history/threads/${currentThreadId}/messages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(copilotApiConfig.headers as Record<string, string>),
            },
            credentials: 'include',
            body: JSON.stringify({ messages: persistable }),
          }
        )
      }
    },
    [setMessages, copilotApiConfig]
  )

  const clearAndAppend = useCallback<FactorialChatRuntime['clearAndAppend']>(
    (msgs) => {
      setCurrentThreadTitle(null)
      setIsLoadingThread(false)

      const hydrated = msgs.map(toCopilotMessage)
      setMessages(hydrated)

      const currentThreadId = threadIdRef.current
      const persistable = filterPersistableMessages(hydrated)
      if (currentThreadId && persistable.length > 0) {
        void runtimeFetchRef.current(
          `${copilotApiConfig.chatApiEndpoint}/chat-history/threads/${currentThreadId}/messages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(copilotApiConfig.headers as Record<string, string>),
            },
            credentials: 'include',
            body: JSON.stringify({ messages: persistable }),
          }
        )
      }
    },
    [setMessages, copilotApiConfig]
  )

  useEffect(() => {
    runtimeFetchRef.current = runtimeFetch
  }, [runtimeFetch])

  return (
    <FactorialChatRuntimeContext.Provider
      value={{
        messages: copilotMessagesToF0(ckMessages),
        inProgress: isLoading,
        threadId: threadId ?? '',
        runtimeFetch,
        onBeforeSendMessage,
        sendMessage,
        appendMessages,
        clearAndAppend,
        clear,
        loadThread,
        currentThreadTitle,
        isLoadingThread,
      }}
    >
      {children}
    </FactorialChatRuntimeContext.Provider>
  )
}

// Returns a no-op fallback when used outside `FactorialOneLayout` so
// components that render in both AI-enabled and AI-disabled trees keep working.
export function useFactorialChatRuntime(): FactorialChatRuntime {
  return useContext(FactorialChatRuntimeContext) ?? NO_PROVIDER_FALLBACK
}
